import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
	addDays,
	format,
	getYear,
	isBefore,
	isSameDay,
	setMonth,
	setYear,
	startOfDay,
	startOfWeek,
} from "date-fns";
import { de } from "date-fns/locale";
import {
	Calendar as CalendarIcon,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import * as React from "react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;

const months = [
	"Januar",
	"Februar",
	"März",
	"April",
	"Mai",
	"Juni",
	"Juli",
	"August",
	"September",
	"Oktober",
	"November",
	"Dezember",
];

const PopoverContent = React.forwardRef<
	React.ElementRef<typeof PopoverPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
	<PopoverPrimitive.Portal>
		<PopoverPrimitive.Content
			ref={ref}
			align={align}
			sideOffset={sideOffset}
			className={cn(
				"z-50 w-[420px] rounded-2xl border border-gray-100 bg-white p-2 text-gray-700 shadow-2xl outline-none animate-in fade-in-0 zoom-in-95",
				className,
			)}
			{...props}
		/>
	</PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

interface CustomDatePickerProps {
	date: Date | undefined;
	onSelect: (date: Date | undefined) => void;
	placeholder?: string;
	className?: string;
}

export function CustomDatePicker({
	date,
	onSelect,
	placeholder = "Datum auswählen",
	className,
}: CustomDatePickerProps) {
	const [currentWeekStart, setCurrentWeekStart] = React.useState(() =>
		startOfWeek(new Date(), { weekStartsOn: 1 }),
	);

	const weekDays = React.useMemo(() => {
		return Array.from({ length: 7 }).map((_, i) =>
			addDays(currentWeekStart, i),
		);
	}, [currentWeekStart]);

	const handlePrevWeek = () => setCurrentWeekStart((prev) => addDays(prev, -7));
	const handleNextWeek = () => setCurrentWeekStart((prev) => addDays(prev, 7));

	const handleMonthChange = (monthIndex: number) => {
		const newDate = setMonth(currentWeekStart, monthIndex);
		setCurrentWeekStart(startOfWeek(newDate, { weekStartsOn: 1 }));
	};

	const handleYearChange = (year: number) => {
		const newDate = setYear(currentWeekStart, year);
		setCurrentWeekStart(startOfWeek(newDate, { weekStartsOn: 1 }));
	};

	const handleToday = () => {
		const today = startOfWeek(new Date(), { weekStartsOn: 1 });
		setCurrentWeekStart(today);
		onSelect(new Date());
	};

	const handleNextWeekShort = () => {
		const nextWeek = startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 });
		setCurrentWeekStart(nextWeek);
		onSelect(addDays(new Date(), 7));
	};

	const today = startOfDay(new Date());
	const currentYear = getYear(currentWeekStart);
	const years = Array.from({ length: 5 }, (_, i) => currentYear + i - 1);

	return (
		<div className={cn("grid gap-2", className)}>
			<Popover>
				<PopoverTrigger asChild>
					<button
						className={cn(
							"flex h-10 w-full md:w-1/2 items-center justify-start rounded-md bg-white/80 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5 transition-all mb-1",
							!date && "text-gray-500",
						)}
					>
						<CalendarIcon className="mr-2 h-4 w-4 text-gray-500" />
						{date ? (
							format(date, "PPP", { locale: de })
						) : (
							<span>{placeholder}</span>
						)}
					</button>
				</PopoverTrigger>
				<PopoverContent className="p-0" align="start">
					<div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
						<div className="flex gap-2 flex-1">
							<Select
								value={currentWeekStart.getMonth().toString()}
								onValueChange={(value) =>
									handleMonthChange(parseInt(value, 10))
								}
							>
								<SelectTrigger className="h-9 border-gray-200 bg-gray-50/50 font-bold text-gray-900 text-sm w-[130px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-white border-gray-100 shadow-xl">
									{months.map((month, i) => (
										<SelectItem
											key={month}
											value={i.toString()}
											className="text-gray-700 focus:bg-gray-100 focus:text-black"
										>
											{month}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							<Select
								value={currentYear.toString()}
								onValueChange={(value) => handleYearChange(parseInt(value, 10))}
							>
								<SelectTrigger className="h-9 border-gray-200 bg-gray-50/50 font-bold text-gray-900 text-sm w-[90px]">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-white border-gray-100 shadow-xl">
									{years.map((year) => (
										<SelectItem
											key={year}
											value={year.toString()}
											className="text-gray-700 focus:bg-gray-100 focus:text-black"
										>
											{year}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex gap-1">
							<button
								onClick={handlePrevWeek}
								className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50"
							>
								<ChevronLeft className="h-4 w-4" />
							</button>
							<button
								onClick={handleNextWeek}
								className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>
					</div>

					<div className="p-4">
						<div className="grid grid-cols-7 gap-2">
							{weekDays.map((day) => {
								const isSelected = date && isSameDay(day, date);
								const isPast = isBefore(startOfDay(day), today);
								const isTodayValue = isSameDay(day, today);
								const isWeekend = day.getDay() === 0 || day.getDay() === 6;

								return (
									<button
										key={day.toISOString()}
										disabled={isPast || isWeekend}
										onClick={() => onSelect(day)}
										className={cn(
											"flex flex-col items-center justify-center py-3 px-1 rounded-xl transition-all border",
											isSelected
												? "bg-black text-white border-black shadow-lg shadow-black/20 scale-105 z-10"
												: "bg-white border-gray-100 hover:border-black/20 hover:bg-gray-50",
											(isPast || isWeekend) &&
												"opacity-20 cursor-not-allowed grayscale",
											isTodayValue &&
												!isSelected &&
												"border-black/40 ring-1 ring-black/10",
										)}
									>
										<span
											className={cn(
												"text-[10px] uppercase font-bold tracking-wider mb-1",
												isSelected ? "text-white/70" : "text-gray-400",
											)}
										>
											{format(day, "eee", { locale: de })}
										</span>
										<span className="text-sm font-bold">
											{format(day, "d")}
										</span>
									</button>
								);
							})}
						</div>
					</div>

					<div className="p-4 border-t border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-b-xl gap-2">
						<div className="flex gap-2">
							<button
								onClick={handleToday}
								className="text-[10px] font-bold uppercase py-2 px-3 rounded-lg border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-all shadow-sm"
							>
								Heute
							</button>
							<button
								onClick={handleNextWeekShort}
								className="text-[10px] font-bold uppercase py-2 px-3 rounded-lg border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-all shadow-sm"
							>
								Nächste Woche
							</button>
						</div>
						<PopoverPrimitive.Close className="text-[10px] font-bold uppercase py-2 px-3 rounded-lg bg-black text-white hover:bg-gray-900 transition-all shadow-sm">
							Fertig
						</PopoverPrimitive.Close>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
}
